import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {HttpCommonService} from '../../../../../shared/services/http-common.service';
import {Observable} from 'rxjs';
import {GenericHttpResponse} from '../../../../../shared/services/interfaces/generic-http-response.interface';
import {Cliente, ClienteFiltros, ClientePayload, ClienteUpdatePayload, Contrato, ContratoCliente, ContratoFiltros, ContratoPayload, ResumenCuentas} from './clientes.interface';
import {Objetivos} from './objetivos/objetivos.interface';
import {DescargaService} from '../../../../../shared/services/descarga.service';

@Injectable({
  providedIn: 'root'
})
export class ClientesService {
  private http = inject(HttpClient);
  private httpCommonService = inject(HttpCommonService);
  private descargaService = inject(DescargaService);

  index(page: number = 1, perPage: number = 15, filtros?: ClienteFiltros): Observable<GenericHttpResponse<any>> {
    const url = this.httpCommonService.makeUrl('/clie/clientes');

    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (filtros) {
      if (filtros.busqueda?.trim()) {
        params = params.set('busqueda', filtros.busqueda.trim());
      }
      if (filtros.tipo?.trim()) {
        params = params.set('tipo', filtros.tipo.trim());
      }
      if (filtros.estado_contrato) {
        params = params.set('estado_contrato', filtros.estado_contrato);
      }
    }

    return this.http.get<GenericHttpResponse<any>>(url, { params });
  }

  show(id: number): Observable<GenericHttpResponse<Cliente>> {
    const url = this.httpCommonService.makeUrl(`/clie/clientes/${id}`);
    return this.http.get<GenericHttpResponse<Cliente>>(url);
  }

  // Se usa el mismo servicio para crear con o sin prospecto, lo que cambia es el body
  store(body: ClientePayload | FormData): Observable<GenericHttpResponse<Cliente>> {
    const url = this.httpCommonService.makeUrl('/clie/clientes');
    return this.http.post<GenericHttpResponse<Cliente>>(url, body);
  }

  // El tipo de persona (F/J) no viaja: el backend lo rechaza porque es inmutable
  update(id: number, body: ClienteUpdatePayload): Observable<GenericHttpResponse<Cliente>> {
    const url = this.httpCommonService.makeUrl(`/clie/clientes/${id}`);
    return this.http.put<GenericHttpResponse<Cliente>>(url, body);
  }

  getContratoActivo(clienteId: number): Observable<GenericHttpResponse<ContratoCliente>> {
    const url = this.httpCommonService.makeUrl(`/clie/clientes/${clienteId}/contrato-activo`);
    return this.http.get<GenericHttpResponse<ContratoCliente>>(url);
  }

  getContratos(clienteId: number, page: number = 1, perPage: number = 15, filtros?: ContratoFiltros): Observable<GenericHttpResponse<any>> {
    const url = this.httpCommonService.makeUrl('/clie/contratos');

    let params = new HttpParams()
      .set('cli_cliente_id', clienteId.toString())
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    if (filtros) {
      if (filtros.busqueda?.trim()) {
        params = params.set('busqueda', filtros.busqueda.trim());
      }
      if (filtros.activo?.trim()) {
        params = params.set('activo', filtros.activo.trim());
      }
      if (filtros.fecha_desde?.trim()) {
        params = params.set('fecha_desde', filtros.fecha_desde.trim());
      }
      if (filtros.fecha_hasta?.trim()) {
        params = params.set('fecha_hasta', filtros.fecha_hasta.trim());
      }
    }

    return this.http.get<GenericHttpResponse<any>>(url, { params });
  }

  getContrato(id: number): Observable<GenericHttpResponse<Contrato>> {
    const url = this.httpCommonService.makeUrl(`/clie/contratos/${id}`);
    return this.http.get<GenericHttpResponse<Contrato>>(url);
  }

  storeContrato(body: ContratoPayload | FormData): Observable<GenericHttpResponse<Contrato>> {
    const url = this.httpCommonService.makeUrl('/clie/contratos');
    return this.http.post<GenericHttpResponse<Contrato>>(url, body);
  }

  updateContrato(id: number, body: Partial<ContratoPayload> | FormData): Observable<GenericHttpResponse<Contrato>> {
    const url = this.httpCommonService.makeUrl(`/clie/contratos/${id}`);

    if (body instanceof FormData) {
      return this.http.post<GenericHttpResponse<Contrato>>(url, body);
    }

    return this.http.put<GenericHttpResponse<Contrato>>(url, body);
  }

  descargarDocumentoContrato(id: number, nombre: string): Observable<string> {
    const url = this.httpCommonService.makeUrl(`/clie/contratos/${id}/documento`);
    return this.descargaService.descargar(url, nombre);
  }

  destroyContrato(id: number): Observable<GenericHttpResponse<Contrato>> {
    const url = this.httpCommonService.makeUrl(`/clie/contratos/${id}`);
    return this.http.delete<GenericHttpResponse<Contrato>>(url);
  }

  // TODO: servicio PLACEHOLDER
  getObjetivos(clienteId: number): Observable<GenericHttpResponse<Objetivos[]>> {
    const url = this.httpCommonService.makeUrl('/clie/objetivos', {cli_cliente_id: clienteId});
    return this.http.get<GenericHttpResponse<Objetivos[]>>(url);
  }

  // TODO: servicio PLACEHOLDER
  getResumenCuentas(clienteId: number): Observable<GenericHttpResponse<ResumenCuentas>> {
    const url = this.httpCommonService.makeUrl(`/clie/clientes/${clienteId}/resumen-cuentas`);
    return this.http.get<GenericHttpResponse<ResumenCuentas>>(url);
  }
}
